/**
 * Secure Logger Utility
 * Provides safe logging that doesn't expose sensitive data in production
 */

const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_DEBUG_MODE === 'true'

// List of sensitive field names that should be redacted
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'apiKey',
  'secret',
  'email',
  'uid',
  'userId',
  'inviteCode',
  'phoneNumber',
  'address',
  'ssn',
  'medicalRecord'
]

/**
 * Redacts sensitive information from objects for safe logging
 */
function redactSensitiveData(data: any): any {
  if (typeof data !== 'object' || data === null) {
    return data
  }

  if (Array.isArray(data)) {
    return data.map(redactSensitiveData)
  }

  const redacted: any = {}
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_FIELDS.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
      redacted[key] = '[REDACTED]'
    } else if (typeof value === 'object') {
      redacted[key] = redactSensitiveData(value)
    } else {
      redacted[key] = value
    }
  }
  
  return redacted
}

/**
 * Safe logger that respects development/production environments
 */
export const secureLogger = {
  debug: (message: string, data?: any) => {
    if (isDevelopment) {
      console.debug(`🐛 ${message}`, data ? redactSensitiveData(data) : '')
    }
  },

  info: (message: string, data?: any) => {
    if (isDevelopment) {
      console.info(`ℹ️ ${message}`, data ? redactSensitiveData(data) : '')
    }
  },

  warn: (message: string, data?: any) => {
    console.warn(`⚠️ ${message}`, data ? redactSensitiveData(data) : '')
  },

  error: (message: string, error?: any) => {
    // Always log errors, but redact sensitive data
    console.error(`❌ ${message}`, error ? redactSensitiveData(error) : '')
  },

  // For critical security events that should always be logged
  security: (message: string, data?: any) => {
    console.warn(`🔒 SECURITY: ${message}`, data ? redactSensitiveData(data) : '')
  }
}

/**
 * Use this instead of console.log for safe logging
 */
export const log = secureLogger.info

export default secureLogger